import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ModalProductComponent } from '@base/components/modal-product/modal-product.component';
import { GlobalState } from '@base/models/others/global-state';
import { SearchReason } from '@base/models/others/match-reason';
import { InformaMfeMatchContext } from '@base/models/others/informa-mfe-event';
import { ProductDetail } from '@base/models/responses/product-detail.res';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { GlobalService } from '@base/services/private/global.service';
import { MeetService } from '@base/services/private/meet.service';
import { SeoService } from '@base/services/private/seo.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';

@Component({
  selector: 'app-product-detail',
  imports: [],
  templateUrl: './product-detail.component.html',
  styleUrl: './product-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailComponent implements AfterViewInit {
  @ViewChild('productDetailContainer', { read: ViewContainerRef }) productDetailContainer!: ViewContainerRef;
  @Input() modalId = '';
  @Input() productId: string | null = null;
  @Input() searchReasons: SearchReason[] | null = null;
  @Input() matchContext: InformaMfeMatchContext | null = null;

  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly meetSrv = inject(MeetService);
  private readonly seoSrv = inject(SeoService);
  private readonly globalSrv = inject(GlobalService);

  productDetailInstance: ComponentRef<unknown> | undefined;
  productDetail: ProductDetail | null = null;
  globalState: GlobalState | null = null;

  isShowPlaceholder = true;

  ngAfterViewInit(): void {
    (async () => {
      await this.loadProductDetailComponent();
    })();
  }

  /**
   * Fetches the product detail by `productId` and updates the component state.
   *
   * @returns void
   */
  getProductDetail(): void {
    if (!this.productId) return;

    this.meetSrv.getProductDetail(this.productId).subscribe({
      next: (res) => {
        if (!res || res.status === 0 || !res.is_viewable) {
          this.globalSrv.destroyModalRef(this.modalId);
          return;
        }
        this.isShowPlaceholder = false;
        // Detail responses normally omit search reasons. The clicked list item passes them in.
        this.productDetail = {
          ...res,
          search_reasons: this.searchReasons ?? res.search_reasons,
        };
        this.renderProductDetail();
        this.setupProductDetailMetaTags();
      },
      error: (err) => {
        console.error('Error fetching product details:', err);
        this.isShowPlaceholder = false;
        this.globalSrv.destroyModalRef(this.modalId);
      },
    });
  }

  /**
   * Sets up SEO meta tags for the product detail page.
   */
  setupProductDetailMetaTags() {
    if (!this.productDetail) return;

    const metaTagObjectArray = [
      {
        property: 'og:url',
        content: window.location.href,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:title',
        content: String(this.productDetail?.title ?? ''),
      },
      {
        property: 'og:description',
        content: String(this.productDetail?.description ?? ''),
      },
      {
        property: 'og:image',
        content: String(this.productDetail?.image_url ?? ''),
      },
    ];

    this.seoSrv.updateMetaTags(metaTagObjectArray);
  }

  /**
   * Dynamically loads the product detail component into the container.
   *
   * @returns A Promise that resolves when the component has been loaded.
   */
  async loadProductDetailComponent(): Promise<void> {
    try {
      const component = await this.dynamicImportLoaderSrv.loadComponent(
        this.productDetailContainer,
        WhiteLabel.COMPONENTS.ModalProduct
      );
      if (component) {
        this.productDetailInstance = component.componentRef || undefined;
        if (this.matchContext) {
          this.productDetailInstance?.setInput('matchContext', this.matchContext);
        }
        this.getProductDetail();
      } else {
        throw new Error('Components could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load components:', error);
    }
  }

  /**
   * Updates and passes input values to the dynamically loaded `ModalProductComponent`.
   *
   * @returns void
   */
  renderProductDetail(): void {
    const component = this.productDetailInstance?.instance as ModalProductComponent;
    if (component) {
      this.productDetailInstance?.setInput('productId', this.productId);
      this.productDetailInstance?.setInput('productDetail', this.productDetail);
      this.productDetailInstance?.setInput('isShowPlaceholder', this.isShowPlaceholder);
      this.productDetailInstance?.setInput('modalId', this.modalId);
      if (this.matchContext) {
        this.productDetailInstance?.setInput('matchContext', this.matchContext);
      }
    }
  }
}
